import React, { useState, useCallback, useEffect } from 'react';
import StudentDetailForm from './components/StudentDetailForm';
import { useForm } from 'react-hook-form';
import FamilyInformationForm from './components/FamilyInformationForm';
import { StudentDetailProps } from './types/studentDetailType';
import { familyTabTypes, studentTabTypes, TabType } from './types';
import {
  RenderTabContentBox,
  RenderTabContentWrapper,
  RoundedButton,
  SaveActionButtonWrapper,
  TabButtonWrapper
} from './styled';
import {
  transformParentFormData,
  transformStudentFormData
} from './utils/transformFormData';
import { AppDispatch, RootState } from '../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSchoolClassesConfig,
  fetchSchoolConfig
} from '../../redux/slices/SchoolConfigSlice';
import studentProfile from '../../apis/studentProfile';
import { SnackbarSeverityType } from '../../enums';
import ToastMessage from '../../components/ToastMessage';
import { TabConfigOptions } from './config/constant';
import { transformStudentDetailData } from '../../utils/studentDataTransformer';
import { Button } from '@mui/material';

const StudentDetailPage = ({
  studentParentProfileData,
  handleRowEdit
}: {
  studentParentProfileData: StudentDetailProps | undefined;
  handleRowEdit: (row: any) => void;
}) => {
  const dispatch: AppDispatch = useDispatch();

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const [selectedTab, setSelectedTab] = useState<TabType>(
    'student_information'
  );

  const [activeTab, setActiveTab] =
    React.useState<familyTabTypes>('motherDetails');
  const [studentActiveTab, setStudentActiveTab] =
    React.useState<studentTabTypes>('studentProfile');

  const { control, setValue, handleSubmit, watch, reset } = useForm({
    defaultValues: studentParentProfileData
  });

  watch();

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );
  const { schoolConfig } = useSelector(
    (state: RootState) => state.schoolConfig
  );

  useEffect(() => {
    if (loggedInUser?.school) {
      dispatch(fetchSchoolConfig(loggedInUser?.school));
      dispatch(fetchSchoolClassesConfig(loggedInUser?.school));
    }
  }, [loggedInUser?.school, dispatch]);

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const updateStudentParentDetailInfo = async (
    id: string,
    data: any,
    which: 'student' | 'guardian' | 'father' | 'mother'
  ): Promise<boolean> => {
    try {
      await studentProfile.updateDetailApi(id, data);
      showToast(
        `Successfully updated ${which} data`,
        SnackbarSeverityType.success
      );
      return true;
    } catch (error: any) {
      showToast(
        error?.errors?.[0]?.message || `Failed to update ${which} data`,
        SnackbarSeverityType.error
      );
      return false;
    }
  };

  const addParentDetail = async (
    studentId: string,
    relation: 'FATHER' | 'MOTHER',
    parentPayload: any
  ): Promise<boolean> => {
    try {
      await studentProfile.addParentDetail(studentId, {
        relation,
        parent: parentPayload
      });
      showToast(
        `Successfully added ${relation.toLowerCase()} data`,
        SnackbarSeverityType.success
      );
      return true;
    } catch (error: any) {
      showToast(
        error?.errors?.[0].messages ||
        `Failed to add ${relation.toLowerCase()} data`,
        SnackbarSeverityType.error
      );
      return false;
    }
  };

  const getStudentDetail = useCallback(
    async (studentId: string) => {
      try {
        const params = {
          studentIds: [studentId]
        };
        const response = await studentProfile.fetchStudentDetailApi(params);

        const transformedStudentDetailData =
          transformStudentDetailData(response);
        reset(transformedStudentDetailData as any);
      } catch (error: any) {
        showToast(
          error?.errors?.[0].messages || 'Error getting student detail',
          SnackbarSeverityType.error
        );
      }
    },
    [reset]
  );

  const handleFormSubmit = async (data: StudentDetailProps) => {
    const { studentDetails, fatherDetails, motherDetails } = data;
    if (
      selectedTab === 'student_information' ||
      (selectedTab === 'family_information' && activeTab === 'guardianDetails')
    ) {
      const which =
        selectedTab === 'student_information' ? 'student' : 'guardian';
      await updateStudentParentDetailInfo(
        studentDetails.id,
        transformStudentFormData(studentDetails, schoolConfig),
        which
      );
      return;
    }

    if (selectedTab === 'family_information') {
      const fatherPayload =
        fatherDetails &&
        transformParentFormData(fatherDetails, studentDetails.school);
      const motherPayload =
        motherDetails &&
        transformParentFormData(motherDetails, studentDetails.school);
      if (activeTab === 'fatherDetails') {
        if (fatherDetails._id) {
          await updateStudentParentDetailInfo(
            fatherDetails._id,
            fatherPayload,
            'father'
          );
        } else if (fatherPayload) {
          await addParentDetail(studentDetails.id, 'FATHER', fatherPayload);
        }
      }

      if (activeTab === 'motherDetails') {
        if (motherDetails._id) {
          await updateStudentParentDetailInfo(
            motherDetails._id,
            motherPayload,
            'mother'
          );
        } else if (motherPayload) {
          await addParentDetail(studentDetails.id, 'MOTHER', motherPayload);
        }
      }
    }
    await getStudentDetail(studentDetails.id);
  };

  const getTabButtonStyles = (tabId: TabType) => ({
    borderRadius: '8px',
    backgroundColor: selectedTab === tabId ? undefined : 'transparent',
    color: selectedTab === tabId ? undefined : '#000000',
    '&:hover': {
      backgroundColor: selectedTab === tabId ? undefined : 'rgba(0, 0, 0, 0.04)'
    }
  });

  const renderTabContent = useCallback(() => {
    switch (selectedTab) {
      case 'family_information':
        return (
          <FamilyInformationForm
            control={control}
            setValue={setValue}
            setActiveTab={setActiveTab}
            activeTab={activeTab}
            showToast={showToast}
          />
        );
      default:
        return (
          <StudentDetailForm
            control={control}
            setValue={setValue}
            showToast={showToast}
            setActiveTab={setStudentActiveTab}
            activeTab={studentActiveTab}
          />
        );
    }
  }, [selectedTab, control, setValue, activeTab, studentActiveTab]);

  return (
    <>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <RenderTabContentWrapper>
          <TabButtonWrapper>
            {TabConfigOptions.map(
              ({
                id,
                label,
                selectedIcon: SelectedIcon,
                unselectedIcon: UnselectedIcon
              }) => {
                const IconComponent =
                  selectedTab === id ? SelectedIcon : UnselectedIcon;
                return (
                  <Button
                    key={id}
                    sx={getTabButtonStyles(id)}
                    onClick={() => setSelectedTab(id)}
                    variant='contained'
                    style={{ boxShadow: 'none' }}
                  >
                    <IconComponent
                      style={{
                        height: '16px',
                        width: '16px',
                        marginRight: 8
                      }}
                    />
                    {label}
                  </Button>
                );
              }
            )}
          </TabButtonWrapper>
        </RenderTabContentWrapper>

        <RenderTabContentWrapper>
          <RenderTabContentBox>{renderTabContent()}</RenderTabContentBox>
          <SaveActionButtonWrapper>
            <RoundedButton onClick={handleRowEdit}>Back</RoundedButton>
            <RoundedButton type='submit'>Save</RoundedButton>
          </SaveActionButtonWrapper>
        </RenderTabContentWrapper>
      </form>
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => setToastOpen(false)}
      />
    </>
  );
};

export default StudentDetailPage;
